import React, { useState } from "react";
import {
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  OutlinedInput,
  Checkbox,
  ListItemText,
  TextField,
  Chip,
  Autocomplete,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { CUSTOM_CHART_CONFIGS } from "../manageData/Analytics/chartConfigs.js";
export default function EmailReport({
  SettingRow,
  formData,
  setFormData,
  handleChange,
  saving,
  sites,
  isLoadingSites,
  reportFrequencyOptions,
  getEmailOptions,
  validateEmail,
  emailError,
  setEmailError,
  inputValue,
  setInputValue,
}) {
  const {t} = useTranslation();
  return (
    <Box className="settings-container">
      <SettingRow
        label={t("Email Frequency")}
        name="reportFrequencyDays"
        value={formData.reportFrequencyDays}
        onChange={handleChange}
        options={reportFrequencyOptions.map((opt) => ({
          code: opt.value,
          name: opt.label,
        }))}
        disabled={saving}
      />
      <SettingRow
        label={t("Site")}
        name="selectedSite"
        value={formData.selectedSite}
        onChange={handleChange}
        options={sites.map((site) => ({
          code: site.id || site.siteId,
          name: site.name || site.siteName,
        }))}
        disabled={saving || isLoadingSites}
      />
      {/* Analytical Types Selection */}
      <Box className="setting-row">
        <Typography className="form-label">
          {t("Analytical graphs to be included")}
        </Typography>
        <Box sx={{ flex: 1 }}>
          <FormControl fullWidth disabled={saving}>
            <Select
              multiple
              name="selectedGraphs"
              value={formData.selectedGraphs.map((graph) => graph.chartId)}
              onChange={(event) => {
                const selectedChartIds = event.target.value;
                const newSelectedGraphs = selectedChartIds.map((chartId) => {
                  const chartConfig = CUSTOM_CHART_CONFIGS.find(
                    (config) => config.id === chartId
                  );
                  return {
                    chartId: chartConfig.id,
                    type: chartConfig.chartType,
                  };
                });
                setFormData((prev) => ({
                  ...prev,
                  selectedGraphs: newSelectedGraphs,
                }));
              }}
              input={<OutlinedInput />}
              renderValue={(selected) => (
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 0.5,
                    maxHeight: "70px",
                    overflowY: "auto",
                  }}
                >
                  {selected.map((chartId) => {
                    const chart = CUSTOM_CHART_CONFIGS.find(
                      (c) => c.id === chartId
                    );
                    return (
                      <Box
                        key={chartId}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          mr: 1,
                          backgroundColor: "#8db3d8ad",
                          padding: "4px 8px",
                          borderRadius: "16px",
                        }}
                      >
                        {chart?.title || chartId}
                      </Box>
                    );
                  })}
                </Box>
              )}
              sx={{
                height: "76px",
                borderRadius: "8px",
                "& .MuiOutlinedInput-root": {
                  minHeight: "48px",
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    maxHeight: 300,
                    overflow: "auto",
                    "& .MuiMenuItem-root": {
                      height: "48px",
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      padding: "0 16px",
                    },
                  },
                },
              }}
            >
              {CUSTOM_CHART_CONFIGS.map((chart) => (
                <MenuItem key={chart.id} value={chart.id}>
                  <Checkbox
                    checked={formData.selectedGraphs.some(
                      (g) => g.chartId === chart.id
                    )}
                  />
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <img
                      src={chart.img}
                      alt={chart.title}
                      style={{
                        width: "40px",
                        height: "40px",
                        objectFit: "contain",
                      }}
                    />
                    <ListItemText primary={chart.title} />
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Receiver's Mail ID Selection */}
      <Box className="setting-row">
        <Typography className="form-label">
          {t("Receiver's Mail ID")}
        </Typography>
        <Box sx={{ flex: 1 }}>
          <Autocomplete
            multiple
            freeSolo
            options={getEmailOptions()}
            filterOptions={(options, { inputValue }) => {
              const filtered = options.filter(
                (option) =>
                  option.label
                    .toLowerCase()
                    .includes(inputValue.toLowerCase()) ||
                  option.email.toLowerCase().includes(inputValue.toLowerCase())
              );

              // If the input looks like an email and isn't already selected, add it as an option
              if (inputValue && validateEmail(inputValue)) {
                const selectedReceivers = formData.selectedReceivers || [];
                const isAlreadySelected = selectedReceivers.some(
                  (email) =>
                    (typeof email === "string" && email === inputValue) ||
                    (typeof email === "object" && email.email === inputValue)
                );

                const existsInOptions = filtered.some(
                  (option) => option.email === inputValue
                );

                if (!isAlreadySelected && !existsInOptions) {
                  filtered.push({
                    email: inputValue,
                    name: inputValue,
                    isOrgUser: false,
                    label: inputValue,
                  });
                }
              }

              return filtered;
            }}
            getOptionLabel={(option) => {
              if (typeof option === "string") {
                return option;
              }
              return option.label || option.email;
            }}
            value={formData.selectedReceivers || []}
            onChange={(event, newValue, reason) => {
              if (reason === "clear") {
                setFormData((prev) => ({ ...prev, selectedReceivers: [] }));
                setEmailError("");
                return;
              }

              const updatedEmails = newValue;
              const invalidEmails = updatedEmails
                .filter((item) => typeof item === "string")
                .filter((email) => !validateEmail(email));

              if (invalidEmails.length > 0) {
                setEmailError(`Invalid email(s): ${invalidEmails.join(", ")}`);
              } else {
                setEmailError("");
                setFormData((prev) => ({
                  ...prev,
                  selectedReceivers: updatedEmails,
                }));
                setInputValue("");
              }
            }}
            inputValue={inputValue}
            onInputChange={(event, newInputValue, reason) => {
              if (reason === "input") {
                setInputValue(newInputValue);
              }
            }}
            renderTags={(value, getTagProps) =>
              (value || []).map((option, index) => {
                const tagProps = getTagProps({ index });
                const isCustomEmail = typeof option === "string";
                return (
                  <Chip
                    key={index}
                    label={isCustomEmail ? option : `${option.name}`}
                    {...tagProps}
                    onDelete={() => {
                      const currentReceivers = formData.selectedReceivers || [];
                      const newEmails = currentReceivers.filter(
                        (_, i) => i !== index
                      );
                      setFormData((prev) => ({
                        ...prev,
                        selectedReceivers: newEmails,
                      }));

                      // Revalidate remaining emails
                      const invalidEmails = newEmails
                        .filter((item) => typeof item === "string")
                        .filter((email) => !validateEmail(email));

                      if (invalidEmails.length > 0) {
                        setEmailError(
                          `Invalid email: ${invalidEmails.join(", ")}`
                        );
                      } else {
                        setEmailError("");
                      }
                    }}
                    sx={{
                      m: 0.5,
                      backgroundColor: isCustomEmail ? "#FFF3CD" : "#E8F4FA",
                      "& .MuiChip-label": {
                        color: "#183059",
                      },
                      "& .MuiChip-deleteIcon": {
                        color: "#183059",
                        "&:hover": {
                          color: "#d32f2f",
                        },
                      },
                    }}
                  />
                );
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                placeholder="Select organization users or type external email addresses"
                error={!!emailError}
                helperText={emailError}
                sx={{
                  mt: 1,
                  "& .MuiOutlinedInput-root": {
                    width: "100%",
                    minHeight: 55,
                    borderRadius: "8px",
                    border: "1px solid #183059",
                    "& .MuiAutocomplete-input": {
                      padding: "8px 12px !important",
                    },
                    "& .MuiAutocomplete-endAdornment": {
                      right: "8px",
                    },
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    border: "none",
                  },
                  "& .MuiFormHelperText-root": {
                    color: emailError ? "#d32f2f" : "#666",
                    fontSize: "12px",
                    mt: 1,
                  },
                }}
              />
            )}
            renderOption={(props, option) => (
              <li {...props}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                  }}
                >
                  <Typography variant="body1">
                    {option.isOrgUser ? option.name : option.email}
                  </Typography>
                  {option.isOrgUser && (
                    <Typography variant="body2" color="text.secondary">
                      {option.email}
                    </Typography>
                  )}
                </Box>
              </li>
            )}
          />
        </Box>
      </Box>
    </Box>
  );
}